import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Lock, Unlock, Clock, ArrowRight, Plus, TrendingUp } from "lucide-react";

const fmtTime = (s) => `${Math.floor(s / 60)}m ${s % 60}s`;

export const History = () => {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/test/history").then((res) => { setAttempts(res.data.attempts); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Header />
      <div className="max-w-4xl mx-auto px-5 py-12">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <div className="uppercase tracking-[0.25em] text-xs font-semibold text-cyan-400 mb-2">Your record</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Attempt history</h1>
          </div>
          <button
            data-testid="new-test-button"
            onClick={() => navigate("/instructions")}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all"
          >
            <Plus className="w-4 h-4" /> New test
          </button>
        </div>

        {loading ? (
          <div className="text-slate-400 font-mono-data text-sm">Loading…</div>
        ) : attempts.length === 0 ? (
          <div className="text-center py-20 rounded-3xl bg-[#131B2A] border border-white/10">
            <p className="text-slate-400">No attempts yet.</p>
            <button onClick={() => navigate("/instructions")} className="mt-4 text-cyan-400 hover:underline">Take your first test →</button>
          </div>
        ) : (
          <>
            {attempts.length >= 2 && (
              <div data-testid="progress-chart" className="mb-8 p-6 rounded-3xl bg-[#131B2A] border border-white/10 fade-up">
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="w-4 h-4 text-cyan-400" />
                  <span className="font-semibold text-slate-200">Your percentile over time</span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={attempts.slice().reverse().map((a, i) => ({
                      label: `#${i + 1}`,
                      percentile: a.teaser.percentile,
                      date: new Date(a.created_at).toLocaleDateString(),
                    }))}
                    margin={{ top: 6, right: 12, left: -16, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                      formatter={(v) => [`${v}th percentile`, ""]}
                      labelFormatter={(l, p) => (p && p[0] ? p[0].payload.date : l)}
                    />
                    <Line type="monotone" dataKey="percentile" stroke="#06B6D4" strokeWidth={3} dot={{ fill: "#6366F1", r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div data-testid="user-history-table" className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/5">
            {attempts.map((a) => (
              <button
                key={a.attempt_id}
                data-testid={`history-row-${a.attempt_id}`}
                onClick={() => navigate(`/results/${a.attempt_id}`)}
                className="w-full flex items-center gap-4 p-5 bg-[#131B2A] hover:bg-[#1A2538] transition-colors text-left group"
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${a.paid ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
                  {a.paid ? <Unlock className="w-5 h-5 text-emerald-400" /> : <Lock className="w-5 h-5 text-amber-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100">
                    {a.paid ? `IQ ${a.result.iq} · ${a.result.band}` : a.teaser.band}
                    {a.paid && a.tier === "premium" && <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Premium</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 font-mono-data">
                    <span>{new Date(a.created_at).toLocaleDateString()} {new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtTime(a.time_taken)}</span>
                    <span>{a.teaser.percentile}th pct</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!a.paid && <span className="text-xs text-amber-300 hidden sm:inline">Unlock</span>}
                  <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
};
